import React from "react";
import MiniChatMessages from "./MiniChatMessages";
import VoiceNotePlayer from "./VoiceNotePlayer";
import styles from "./MiniChat.module.css";

function fileIcon(type) {
  if (type.startsWith("image/")) return "fa-image";
  if (type.startsWith("video/")) return "fa-film";
  if (type.startsWith("audio/")) return "fa-music";
  return "fa-file";
}

function formatVoiceTimer(durationMs) {
  const totalSeconds = Math.max(0, Math.floor((Number(durationMs) || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${`${seconds}`.padStart(2, "0")}`;
}

export default function MiniChatView({
  cancelVoiceNoteRecording,
  chatId,
  clearVoiceNotePreview,
  closeMiniChat,
  conversationId,
  copiedMessageKey,
  currentUser,
  draft,
  dragging,
  endRef,
  error,
  fileInputRef,
  filePreviews,
  friend,
  friendName,
  handleAttachmentLayout,
  handleDragEnter,
  handleDragLeave,
  handleDragOver,
  handleDrop,
  handleFileSelect,
  handleKeyDown,
  handleMessageCopied,
  handlePaste,
  handleSend,
  hasPendingVoiceNote,
  inputRef,
  isVoiceNoteProcessing,
  isVoiceNoteRecording,
  loading,
  messageAreaRef,
  messages,
  minimized,
  pendingVoiceNoteDurationMs,
  pendingVoiceNoteUrl,
  removeFile,
  rightPosition,
  selectedFiles,
  sending,
  setDraft,
  startVoiceNoteRecording,
  stopVoiceNoteRecording,
  toggleMinimize,
  uploadProgress,
  voiceNoteElapsedMs,
  voiceNotesSupported
}) {
  return (
    <div
      className={`${styles.miniChat} ${minimized ? styles.minimized : ""}`}
      style={{ right: rightPosition }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className={styles.header} onClick={() => toggleMinimize(chatId)}>
        <img
          src={friend.photo || "/images/default_pic.png"}
          alt=""
          className={styles.headerAvatar}
        />
        <span className={styles.headerName}>{friendName}</span>
        <span className={styles.onlineDot} />
        <span className={styles.headerActions}>
          <button
            className={styles.headerBtn}
            onClick={(e) => {
              e.stopPropagation();
              toggleMinimize(chatId);
            }}
            title={minimized ? "Expand" : "Minimize"}
          >
            <i
              className={`fa-solid ${
                minimized ? "fa-chevron-up" : "fa-chevron-down"
              }`}
              style={{ fontSize: 11 }}
            />
          </button>
          <button
            className={styles.headerBtn}
            onClick={(e) => {
              e.stopPropagation();
              closeMiniChat(chatId);
            }}
            title="Close"
          >
            <i className="fa-solid fa-xmark" style={{ fontSize: 13 }} />
          </button>
        </span>
      </div>

      {/* ── Message area ─────────────────────────────────────────── */}
      {!minimized && (
        <>
          <MiniChatMessages
            copiedMessageKey={copiedMessageKey}
            currentUser={currentUser}
            dragging={dragging}
            endRef={endRef}
            error={error}
            friendName={friendName}
            handleAttachmentLayout={handleAttachmentLayout}
            handleMessageCopied={handleMessageCopied}
            loading={loading}
            messageAreaRef={messageAreaRef}
            messages={messages}
          />

          {isVoiceNoteRecording ? (
            <div className={styles.recorderStrip}>
              <div className={styles.recorderMeta}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#d93025",
                    flexShrink: 0,
                  }}
                />
                <strong style={{ fontSize: 12, color: "#8b1e23" }}>
                  Recording
                </strong>
                <span style={{ fontSize: 12, color: "#666" }}>
                  {formatVoiceTimer(voiceNoteElapsedMs)}
                </span>
              </div>
              <div className={styles.recorderActions}>
                <button
                  type="button"
                  className={styles.recorderActionBtn}
                  onClick={cancelVoiceNoteRecording}
                  title="Cancel recording"
                >
                  <span>Cancel</span>
                  <i className="fa-solid fa-xmark" />
                </button>
                <button
                  type="button"
                  className={`${styles.recorderActionBtn} ${styles.recorderActionBtnDanger}`}
                  onClick={stopVoiceNoteRecording}
                  title="Stop recording"
                >
                  <span>Stop</span>
                  <i className="fa-solid fa-stop" />
                </button>
              </div>
            </div>
          ) : null}

          {hasPendingVoiceNote && pendingVoiceNoteUrl ? (
            <div className={styles.recorderStrip}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <VoiceNotePlayer
                  src={pendingVoiceNoteUrl}
                  durationMs={pendingVoiceNoteDurationMs}
                  compact
                  label="Voice note preview"
                />
              </div>
              <button
                type="button"
                className={styles.recorderActionBtn}
                onClick={clearVoiceNotePreview}
                title="Discard voice note"
              >
                <span>Discard</span>
                <i className="fa-solid fa-trash" />
              </button>
            </div>
          ) : null}

          {/* ── File previews ──────────────────────────────────────── */}
          {filePreviews.length > 0 && (
            <div className={styles.filePreviews}>
              {filePreviews.map((fp, i) => (
                <div key={i} className={styles.filePreviewItem}>
                  {fp.url ? (
                    <img
                      src={fp.url}
                      alt=""
                      className={styles.filePreviewImg}
                    />
                  ) : (
                    <div className={styles.filePreviewGeneric}>
                      <i className={`fa-solid ${fileIcon(fp.type)}`} />
                    </div>
                  )}
                  <button
                    className={styles.filePreviewRemove}
                    onClick={() => removeFile(i)}
                    title="Remove"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Upload progress ─────────────────────────────────────── */}
          {sending && selectedFiles.length > 0 && (
            <div className={styles.uploadBar}>
              <div
                className={styles.uploadBarFill}
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {/* ── Input area ───────────────────────────────────────── */}
          <form className={styles.inputArea} onSubmit={handleSend}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className={styles.attachBtn}
              onClick={() => fileInputRef.current?.click()}
              title="Attach file"
              disabled={
                !conversationId ||
                loading ||
                isVoiceNoteRecording ||
                isVoiceNoteProcessing
              }
            >
              <i className="fa-solid fa-paperclip" style={{ fontSize: 14 }} />
            </button>
            <textarea
              ref={inputRef}
              className={styles.inputField}
              placeholder={
                isVoiceNoteRecording
                  ? "Recording voice note…"
                  : hasPendingVoiceNote
                    ? "Send your voice note or discard it"
                    : "Type a message"
              }
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              disabled={
                !conversationId ||
                loading ||
                isVoiceNoteRecording ||
                isVoiceNoteProcessing ||
                hasPendingVoiceNote
              }
              rows={1}
            />
            {voiceNotesSupported &&
            !draft.trim() &&
            selectedFiles.length === 0 &&
            !hasPendingVoiceNote &&
            !isVoiceNoteRecording ? (
              <button
                type="button"
                className={styles.attachBtn}
                onClick={startVoiceNoteRecording}
                title="Record voice note"
                disabled={!conversationId || loading || isVoiceNoteProcessing}
              >
                <i className="fa-solid fa-microphone" style={{ fontSize: 14 }} />
              </button>
            ) : null}
            <button
              type="submit"
              className={styles.sendBtn}
              disabled={
                (!draft.trim() &&
                  selectedFiles.length === 0 &&
                  !hasPendingVoiceNote) ||
                !conversationId ||
                sending ||
                isVoiceNoteRecording ||
                isVoiceNoteProcessing
              }
              title="Send"
            >
              <i className="fa-solid fa-paper-plane" style={{ fontSize: 13 }} />
            </button>
          </form>

          {/* ── Error toast ──────────────────────────────────────── */}
          {error && messages.length > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: 50,
                left: 10,
                right: 10,
                background: "#e53935",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: 6,
                fontSize: 11,
                textAlign: "center",
              }}
            >
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
}
